import { useCallback, useLayoutEffect, useRef } from "react";

import { getContextClassnameForGrid } from "./useContextTree";

export interface CellNavigationOptions {
  rowCount?: number;
  colCount?: number | ((rowIndex: number) => number);
  scrollOffsetTop?:
    | number
    | (() => number)
    | ((targetRowIndex: number) => number);
  scrollOffsetLeft?:
    | number
    | (() => number)
    | ((targetColIndex: number) => number);
  scrollBuffer?: number;
  wrap?: boolean;
}

export interface CellProps {
  ref: (el: HTMLElement | null) => void;
  className: string | undefined;
  "data-row-index": number;
  "data-col-index": number;
}

// Overflow values that indicate a scroll container
const SCROLL_VALUES: ReadonlySet<string> = new Set(["auto", "scroll"]);

function findScrollContainer(start: HTMLElement | null): HTMLElement | null {
  let el = start;
  while (el) {
    const style = getComputedStyle(el);
    if (
      SCROLL_VALUES.has(style.overflowY) ||
      SCROLL_VALUES.has(style.overflowX) ||
      SCROLL_VALUES.has(style.overflow)
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function resolveOffset(
  offset: number | (() => number) | ((idx: number) => number) | undefined,
  index: number,
): number {
  if (offset === undefined) return 0;
  if (typeof offset === "number") return offset;
  return offset(index);
}

export function useCellRegistry(
  contextId: string,
  elementRef: React.RefObject<HTMLElement | null>,
) {
  const cellRegistryRef = useRef<Map<string, HTMLElement>>(new Map());
  /** Logical column for ArrowUp/Down; survives clamping to shorter rows, cleared on Left/Right or pointer down. */
  const verticalNavAnchorColRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const root = elementRef.current;
    if (!root) return;
    const clearAnchor = () => {
      verticalNavAnchorColRef.current = null;
    };
    root.addEventListener("mousedown", clearAnchor, true);
    return () => {
      root.removeEventListener("mousedown", clearAnchor, true);
    };
  }, [contextId, elementRef]);

  const getCell = useCallback((rowIndex: number, colIndex: number) => {
    return cellRegistryRef.current.get(`${rowIndex}-${colIndex}`) ?? null;
  }, []);

  const getMaxColInRow = useCallback((rowIndex: number) => {
    let maxCol = -1;
    for (const key of cellRegistryRef.current.keys()) {
      const dash = key.indexOf("-");
      const r = +key.substring(0, dash);
      if (r === rowIndex) {
        const c = +key.substring(dash + 1);
        if (c > maxCol) maxCol = c;
      }
    }
    return maxCol;
  }, []);

  const getMaxRow = useCallback(() => {
    let maxRow = -1;
    for (const key of cellRegistryRef.current.keys()) {
      const r = +key.substring(0, key.indexOf("-"));
      if (r > maxRow) maxRow = r;
    }
    return maxRow;
  }, []);

  const getCellProps = useCallback(
    ({
      rowIndex,
      colIndex,
      objectKey,
    }: {
      rowIndex: number;
      colIndex: number;
      objectKey?: string;
    }): CellProps => {
      const key = `${rowIndex}-${colIndex}`;
      return {
        ref: (el: HTMLElement | null) => {
          if (el) {
            cellRegistryRef.current.set(key, el);
          } else {
            cellRegistryRef.current.delete(key);
          }
        },
        className: objectKey
          ? getContextClassnameForGrid(objectKey, contextId)
          : undefined,
        "data-row-index": rowIndex,
        "data-col-index": colIndex,
      };
    },
    [contextId],
  );

  const createArrowKeyHandler = useCallback(
    (
      direction: "up" | "down" | "left" | "right",
      navOptions?: CellNavigationOptions,
    ) => {
      return (e: KeyboardEvent) => {
        const activeElement = document.activeElement as HTMLElement;
        if (!activeElement) return;

        const contextContainer = elementRef.current;
        if (!contextContainer?.contains(activeElement)) return;

        let cellWrapper: HTMLElement | null = activeElement;
        while (cellWrapper && !cellWrapper.dataset.rowIndex) {
          cellWrapper = cellWrapper.parentElement;
          if (
            cellWrapper &&
            !cellWrapper.closest(`[data-context-id="${contextId}"]`)
          ) {
            return;
          }
        }
        if (!cellWrapper) return;

        const row = parseInt(cellWrapper.dataset.rowIndex ?? "", 10);
        const col = parseInt(cellWrapper.dataset.colIndex ?? "", 10);
        if (isNaN(row) || isNaN(col)) return;

        e.preventDefault();
        e.stopPropagation();

        const maxRow =
          navOptions?.rowCount !== undefined
            ? navOptions.rowCount - 1
            : getMaxRow();
        const currentMaxCol = getMaxColInRow(row);

        let nextRow = row;
        let nextCol = col;
        const shouldWrap = navOptions?.wrap !== false;
        let verticalMoveAnchor: number | undefined;

        switch (direction) {
          case "up": {
            if (row > 0) {
              nextRow = row - 1;
            } else if (shouldWrap) {
              nextRow = maxRow;
            }
            const anchor = verticalNavAnchorColRef.current ?? col;
            verticalMoveAnchor = anchor;
            const upActualMaxCol = getMaxColInRow(nextRow);
            nextCol =
              upActualMaxCol >= 0 ? Math.min(anchor, upActualMaxCol) : 0;
            break;
          }

          case "down": {
            if (row < maxRow) {
              nextRow = row + 1;
            } else if (shouldWrap) {
              nextRow = 0;
            }
            const anchor = verticalNavAnchorColRef.current ?? col;
            verticalMoveAnchor = anchor;
            const downActualMaxCol = getMaxColInRow(nextRow);
            nextCol =
              downActualMaxCol >= 0 ? Math.min(anchor, downActualMaxCol) : 0;
            break;
          }

          case "left": {
            if (col > 0) {
              nextCol = col - 1;
            } else if (row > 0) {
              nextRow = row - 1;
              const leftMaxCol = getMaxColInRow(nextRow);
              nextCol = leftMaxCol >= 0 ? leftMaxCol : 0;
            } else if (shouldWrap) {
              nextRow = maxRow;
              const wrapMaxCol = getMaxColInRow(nextRow);
              nextCol = wrapMaxCol >= 0 ? wrapMaxCol : 0;
            }
            break;
          }

          case "right": {
            if (col < currentMaxCol) {
              nextCol = col + 1;
            } else if (row < maxRow) {
              nextRow = row + 1;
              nextCol = 0;
            } else if (shouldWrap) {
              nextRow = 0;
              nextCol = 0;
            }
            break;
          }
        }

        if (nextRow < 0) nextRow = 0;
        if (nextCol < 0) nextCol = 0;

        const nextCell = cellRegistryRef.current.get(`${nextRow}-${nextCol}`);
        if (!nextCell) return;

        if (verticalMoveAnchor !== undefined) {
          verticalNavAnchorColRef.current = verticalMoveAnchor;
        } else {
          verticalNavAnchorColRef.current = null;
        }

        nextCell.focus({ preventScroll: true });

        const scrollContainer = findScrollContainer(nextCell.parentElement);
        if (!scrollContainer) return;

        const scrollOffsetTop = resolveOffset(
          navOptions?.scrollOffsetTop,
          nextRow,
        );
        const scrollOffsetLeft = resolveOffset(
          navOptions?.scrollOffsetLeft,
          nextCol,
        );

        const cellRect = nextCell.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const bufferHeight = (navOptions?.scrollBuffer ?? 1) * cellRect.height;

        if (direction === "up") {
          const targetTop = containerRect.top + scrollOffsetTop + bufferHeight;
          if (cellRect.top < targetTop) {
            scrollContainer.scrollBy({
              top: cellRect.top - targetTop,
              behavior: "auto",
            });
          }
        } else if (direction === "down") {
          const targetBottom = containerRect.bottom - bufferHeight;
          if (cellRect.bottom > targetBottom) {
            scrollContainer.scrollBy({
              top: cellRect.bottom - targetBottom,
              behavior: "auto",
            });
          }
        } else if (direction === "left") {
          const visibleLeft = containerRect.left + scrollOffsetLeft;
          if (cellRect.left < visibleLeft) {
            scrollContainer.scrollBy({
              left: cellRect.left - visibleLeft,
              behavior: "auto",
            });
          }
        } else if (direction === "right") {
          const visibleLeft = containerRect.left + scrollOffsetLeft;
          const visibleRight = containerRect.right;
          if (cellRect.left < visibleLeft) {
            scrollContainer.scrollBy({
              left: cellRect.left - visibleLeft,
              behavior: "auto",
            });
          } else if (cellRect.right > visibleRight) {
            scrollContainer.scrollBy({
              left: cellRect.right - visibleRight,
              behavior: "auto",
            });
          }
        }
      };
    },
    [contextId, getMaxRow, getMaxColInRow],
  );

  const getClassName = useCallback(
    (objectKey: string) => getContextClassnameForGrid(objectKey, contextId),
    [contextId],
  );

  return { getCell, getCellProps, createArrowKeyHandler, getClassName };
}
